/* The index loader verifies completeness before any engine result is displayed. */
const dimensions = {
  shape: { param: 'filter.p.m.custom.item_types', label: '形状' },
  leather: { param: 'filter.p.m.custom.outer_material', label: '外装の革' },
  series: { param: 'filter.p.m.custom.series', label: 'シリーズ' },
  color: { param: 'filter.v.option.color', label: 'カラー' }
};
const abortError = () => new DOMException('Aborted', 'AbortError');
function current(signal) { if (signal?.aborted) throw abortError(); }
function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}
export function readSearchState(url, pageSize = 24) {
  const params = new URL(url, 'https://example.invalid').searchParams;
  const filters = {}, unsupported = [];
  for (const [name, value] of params) {
    if (!value || !name.startsWith('filter.')) continue;
    let key = Object.keys(dimensions).find(k => dimensions[k].param === name);
    if (/^filter\.v\.option\.(color|colour|カラー|色)$/i.test(name)) key = 'color';
    if (key) (filters[key] ||= []).push(value);
    else if (name === 'filter.v.price.gte') { if (!Number.isFinite(Number(value)) || Number(value) < 0) unsupported.push(name); else filters.minPrice = Number(value) * 100; }
    else if (name === 'filter.v.price.lte') { if (!Number.isFinite(Number(value)) || Number(value) < 0) unsupported.push(name); else filters.maxPrice = Number(value) * 100; }
    else if (name === 'filter.v.availability') { const values = params.getAll(name); if (!(values.includes('1') && values.includes('0'))) filters.available = value === '1'; }
    else (filters.native ||= {})[name] = params.getAll(name);
  }
  return { query: params.get('q') || '', filters, unsupported, options: { page: Math.max(1, Number(params.get('page')) || 1), pageSize, sort: params.get('sort_by') || 'best-selling' } };
}
export function searchLink(searchUrl, query, sourceUrl) {
  const url = new URL(searchUrl, sourceUrl || location.href);
  // Keep the preview/locale route while retaining the user's original input.
  if (sourceUrl) {
    const source = new URL(sourceUrl);
    for (const name of ['preview_theme_id', '_ab', '_fd', '_sc']) if (source.searchParams.has(name)) url.searchParams.set(name, source.searchParams.get(name));
  }
  url.searchParams.set('q', query);
  return url;
}
export function resultMessage(result) {
  if (result.suggestions?.some(s => s.kind === 'intent-alternative')) return '検索候補から条件を選んで、商品をお探しください。';
  const terms = result.unresolved?.map(x => x.term).filter(Boolean) || [];
  if (terms.length) return `検索語「${terms.join('、')}」を特定できませんでした。入力を確認してください。`;
  if (result.state === 'ambiguous') return '複数の意味があります。条件を選んで検索を続けられます。';
  if (result.state === 'out_of_assortment') return '指定された条件の商品は取扱いがありません。';
  return '';
}
function money(value) { return new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY', maximumFractionDigits: 0 }).format(Number(value) / 100); }
function appendSuggestions(parent, result, searchUrl) {
  if (!result.suggestions?.length) return;
  const group = element('div', 'predictive-search__resource-item');
  group.append(element('p', 'predictive-search__category h6 text-subdued', '検索候補'));
  const links = element('div', 'predictive-search__suggestions scroll-area bleed md:unbleed');
  for (const suggestion of result.suggestions) {
    const link = element('a', 'link-reverse', suggestion.label || suggestion.query);
    link.href = searchLink(searchUrl, suggestion.query, location.href).href;
    if (suggestion.reason) link.append(element('span', 'text-subdued', `（${suggestion.reason}）`));
    links.append(link);
  }
  group.append(links); parent.append(group);
}
function predictiveProduct(product, ids, trackedUrl) {
  const eligible = product.variants.filter(v => ids?.includes(String(v.id)));
  const variant = eligible.find(v => v.available) || eligible[0] || product.variants.find(v => v.available) || product.variants[0];
  const link = element('a', `cm-search-product${product.comment ? ' cm-search-product--with-comment' : ''}`);
  const url = new URL(trackedUrl || product.url, location.href);
  if (variant?.id) url.searchParams.set('variant', variant.id);
  link.href = url.href;
  const media = element('span', 'cm-search-product__image');
  const selectedImage = variant?.image || product.image;
  if (selectedImage?.url) {
    const image = element('img'); image.src = selectedImage.url; image.alt = selectedImage.alt || ''; image.loading = 'lazy'; image.width = 80; image.height = 80;
    media.append(image);
  }
  const info = element('span', 'cm-search-product__info');
  info.append(element('span', 'cm-search-product__title', product.title));
  if (product.comment) info.append(element('span', 'cm-search-product__comment', product.comment));
  const price = element('price-list', 'price-list');
  const sale = Number(variant?.compareAt) > Number(variant?.price);
  const amount = element('sale-price', sale ? 'text-on-sale' : 'text-subdued', money(variant?.price || 0)); amount.prepend(element('span', 'sr-only', '販売価格')); price.append(amount);
  if (sale) { const compare = element('compare-at-price', 'text-subdued line-through', money(variant.compareAt)); compare.prepend(element('span', 'sr-only', '通常価格')); price.append(compare); }
  info.append(price);
  const arrow = element('span', 'cm-search-product__arrow', '→'); arrow.setAttribute('aria-hidden', 'true');
  link.append(media, info, arrow); return link;
}
export async function renderPredictiveResults({ query, loaderUrl, searchUrl, signal, supportsPredictive }) {
  const nativeUrl = new URL(searchUrl + (supportsPredictive ? '/suggest' : ''), location.href);
  nativeUrl.searchParams.set('q', query); nativeUrl.searchParams.set('section_id', 'c-predictive-search');
  if (supportsPredictive) {
    nativeUrl.searchParams.set('resources[type]', 'product,collection,article,page');
    nativeUrl.searchParams.set('resources[limit]', '10'); nativeUrl.searchParams.set('resources[limit_scope]', 'each');
  } else nativeUrl.searchParams.set('type', 'article,page');
  const [engine, response] = await Promise.all([
    import(loaderUrl).then(loader => loader.loadSearchEngine({ signal })),
    fetch(nativeUrl, { signal }).then(r => { if (!r.ok) throw new Error('Non-product search failed'); return r.text(); })
  ]);
  current(signal);
  const result = engine.search(query, {}, { page: 1, pageSize: 3 });
  const doc = new DOMParser().parseFromString(response, 'text/html');
  const section = doc.querySelector('.shopify-section');
  if (!section) throw new Error('Predictive section missing');
  // Native non-product content survives; product and query judgments belong to the engine.
  section.querySelectorAll('.predictive-search__suggestions').forEach(node => node.closest('.predictive-search__resource-item')?.remove());
  const trackedUrls = new Map();
  section.querySelectorAll('.cm-search-product[href]').forEach(link => { const url = new URL(link.getAttribute('href'), location.href); trackedUrls.set(url.pathname.split('/').pop(), url.href); });
  const nativeProducts = section.querySelector('.predictive-search__products');
  if (nativeProducts) {
    const panel = nativeProducts.closest('[role="tabpanel"]');
    const tabs = panel?.parentElement;
    if (panel && tabs) { const index = Array.from(tabs.querySelectorAll(':scope > [slot="content"]')).indexOf(panel); tabs.querySelectorAll(':scope > [slot="title"]')[index]?.remove(); panel.remove(); }
  }
  section.querySelector('.predictive-search__no-results')?.remove();
  let wrapper = section.querySelector('.predictive-search__results');
  if (!wrapper) { wrapper = element('div', 'predictive-search__results'); section.append(wrapper); }
  const headline = element('div');
  appendSuggestions(headline, result, searchUrl);
  const message = resultMessage(result);
  if (message) headline.append(element('p', 'cm-search-status', message));
  let tabs = wrapper.querySelector('x-tabs');
  if (result.products.length) {
    if (!tabs) {
      const template = document.createElement('template');
      template.innerHTML = '<x-tabs class="predictive-search__tabs content-tabs"><template shadowrootmode="open"><div part="tab-list-scrollable"><slot role="tablist" part="tab-list" name="title"></slot></div><slot part="tab-panels" name="content"></slot></template></x-tabs>';
      tabs = template.content.firstElementChild; wrapper.append(tabs);
    }
    const title = element('button', 'h6', '商品'); title.type = 'button'; title.setAttribute('slot', 'title'); title.setAttribute('role', 'tab');
    const panel = element('div', 'predictive-search__resource-item'); panel.setAttribute('slot', 'content'); panel.setAttribute('role', 'tabpanel');
    const products = element('div', 'predictive-search__products'); products.style.setProperty('--product-list-items-per-row', '3');
    result.products.forEach(p => products.append(predictiveProduct(p, result.matchedVariantIds?.[p.handle], trackedUrls.get(p.handle))));
    panel.append(products); tabs.querySelectorAll(':scope > [slot="content"]').forEach(p => p.hidden = true);
    const firstTitle = tabs.querySelector(':scope > [slot="title"]'); const firstContent = tabs.querySelector(':scope > [slot="content"]');
    tabs.insertBefore(title, firstTitle); tabs.insertBefore(panel, firstContent);
  } else {
    const panels = tabs?.querySelectorAll(':scope > [slot="content"]'); if (panels?.length) panels[0].hidden = false;
    headline.append(element('p', 'cm-search-status', '該当する商品はありません。'));
  }
  const all = element('a', 'link', `全件を見る（商品${result.total}件）`); all.href = searchLink(searchUrl, query, location.href).href;
  headline.append(all); wrapper.prepend(headline);
  return section;
}
function escapeSearchCard(value) {
  return String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]);
}
function searchCardImageUrl(value, width) {
  const url = String(value || '');
  if (!url || /^(?:javascript|data|vbscript):/i.test(url.trim())) throw new Error('Product card image invalid');
  return /([?&])width=\d+(?=&|$)/.test(url) ? url.replace(/([?&])width=\d+(?=&|$)/, `$1width=${width}`) : `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
}
function searchCardTrustedMarkup(value) {
  const html = String(value || '');
  if (/<\s*(?:script|iframe|object|embed|style|link|meta|base)\b|\bon[a-z]+\s*=|(?:javascript|vbscript)\s*:/i.test(html)) throw new Error('Product card markup invalid');
  return html;
}
function renderSearchCardHtml(product) {
  const data = product.card;
  if (!data || data.v !== 1) return null;
  const escape = escapeSearchCard;
  const variants = new Map(product.variants.map(variant => [String(variant.id), variant]));
  const initial = variants.get(String(data.initialVariantId));
  if (!initial || !Array.isArray(data.imageVariantIds) || !Array.isArray(data.swatches)) throw new Error('Product card data invalid');
  const image = (source, id, alt, active) => {
    const url = source?.url;
    if (!url) throw new Error('Product card image missing');
    return `<img src="${escape(searchCardImageUrl(url, 320))}" srcset="${escape(searchCardImageUrl(url, 160))} 160w, ${escape(searchCardImageUrl(url, 320))} 320w, ${escape(searchCardImageUrl(url, 480))} 480w" sizes="(max-width: 699px) 112px, 160px" alt="${escape(alt)}" class="c-collection-card__image${active ? ' c-collection-card__image--active' : ''} cm-search-result-card__image" loading="lazy" draggable="false" width="160" height="160"${active ? ' data-default-image' : ' style="display: none;"'} data-variant-id="${escape(id)}">`;
  };
  const initialImage = data.initialImage || initial.image || product.image;
  let images = initialImage ? image(initialImage, initial.id, product.title, true) : searchCardTrustedMarkup(data.placeholderHtml);
  for (const id of data.imageVariantIds) {
    const variant = variants.get(String(id));
    if (!variant) throw new Error('Product card image variant invalid');
    images += image(variant.image, variant.id, variant.title, false);
  }
  const onclick = 'event.preventDefault(); event.stopPropagation();';
  const swatches = data.swatches.map(swatch => {
    const variant = swatch.id ? variants.get(String(swatch.id)) : null;
    if (swatch.id && !variant) throw new Error('Product card swatch variant invalid');
    const preload = variant?.image ? ` data-search-preload-src="${escape(searchCardImageUrl(variant.image.url, 320))}"` : '';
    const classes = `c-collection-card__swatch color-swatch${data.rounded ? ' rounded-full' : ''}${swatch.border ? ' border' : ''}${swatch.disabled ? ' is-disabled' : ''}${swatch.active ? ' c-collection-card__swatch--active' : ''}`;
    return `<button type="button" class="${classes}"${swatch.id ? ` data-variant-id="${escape(swatch.id)}"` : ''} data-color-value="${escape(swatch.name)}"${preload} aria-label="${escape(swatch.name)}" style="--swatch-background: ${escape(swatch.background)}; --swatch-size: 1.5rem; --swatch-offset: 1px;"><span class="sr-only">${escape(swatch.name)}</span></button>`;
  }).join('');
  const swatchMarkup = data.hasSwatches || data.swatches.length ? `<div class="c-color-swatches-scroll c-collection-card__swatches-scroll" data-c-color-swatches-scroll onclick="${onclick}"><div class="c-collection-card__swatches" data-swatches-scroll onclick="${onclick}">${swatches}</div><div class="c-color-swatches-scrollbar" data-c-color-swatches-scrollbar><div class="c-color-swatches-scrollbar__thumb" data-c-color-swatches-scrollbar-thumb></div></div></div>` : '';
  const tag = data.tagBadge ? `<div class="c-collection-card-tag-badge"><svg class="c-collection-card-tag-badge__icon" width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M8 0C3.58 0 0 3.58 0 8C0 12.42 3.58 16 8 16C12.42 16 16 12.42 16 8C16 3.58 12.42 0 8 0ZM8.8 12H7.2V7.2H8.8V12ZM8.8 5.6H7.2V4H8.8V5.6Z" fill="#241A00"/></svg><span class="c-collection-card-tag-badge__text">${data.tagBadge === 'restock' ? '再入荷しました' : '販売開始前'}</span></div>` : '';
  return `<product-list class="product-list cm-search-result-row"><product-card class="c-collection-card product-card cm-search-result-card" handle="${escape(product.handle)}" data-initial-variant-id="${escape(initial.id)}"><div class="c-collection-card__image-wrapper product-card__figure cm-search-result-card__media"><a href="${escape(product.url)}" class="c-collection-card__image-link product-card__media" draggable="false" data-instant><div class="c-collection-card__image-container">${images}</div></a><div class="c-collection-card__badges">${searchCardTrustedMarkup(data.badgesHtml)}${tag}</div></div><div class="c-collection-card__info cm-search-result-card__info">${searchCardTrustedMarkup(data.salesHtml)}<h3 class="c-collection-card__title"><a href="${escape(product.url)}" data-instant>${escape(product.title)}</a></h3><p class="c-collection-card__price">${escape(data.price)}<span>（税込）</span></p>${product.comment ? `<p class="c-collection-card__search-comment">${escape(product.comment)}</p>` : ''}<div class="c-collection-card__swatches-wrapper">${swatchMarkup}<p class="cm-search-result-card__selected-color" data-search-selected-color aria-live="polite" aria-atomic="true"${data.initialColor ? '' : ' hidden'}><span class="cm-search-result-card__color-caption">カラー</span><span data-search-color-name>${escape(data.initialColor)}</span></p></div></div></product-card></product-list>`;
}

// Warm the actual responsive image, after first paint, only near the viewport.
// Selection remains synchronous in c-common.js; image completion never changes it.
function prepareSearchCardImages(list) {
  const cards = Array.from(list.querySelectorAll('.cm-search-result-card'));
  const near = new Set(), queued = new Set(), warmed = new WeakMap(), pending = [];
  let stopped = false, running = 0, scheduled = false;
  const warm = (image, priority) => {
    if (!image) return Promise.resolve();
    if (priority === 'high' || !warmed.has(image)) image.fetchPriority = priority;
    if (warmed.has(image)) return warmed.get(image);
    const ready = new Promise(resolve => {
      const finish = () => {
        image.removeEventListener('load', finish); image.removeEventListener('error', finish);
        resolve();
      };
      image.addEventListener('load', finish, { once: true });
      image.addEventListener('error', finish, { once: true });
      image.loading = 'eager';
      if (image.complete) finish();
    });
    warmed.set(image, ready);
    return ready;
  };
  const run = () => {
    scheduled = false;
    if (stopped) return;
    for (let index = 0; index < pending.length && running < 2;) {
      const image = pending[index], card = image.closest('.cm-search-result-card');
      if (!image.isConnected || !near.has(card) || warmed.has(image)) {
        pending.splice(index, 1); queued.delete(image); continue;
      }
      const active = card.querySelector('.c-collection-card__image--active');
      if (active && !active.complete) { index++; continue; }
      pending.splice(index, 1); queued.delete(image); running++;
      warm(image, 'low').finally(() => { running--; schedule(); });
    }
  };
  const schedule = () => {
    if (stopped || scheduled || !pending.length) return;
    scheduled = true;
    requestAnimationFrame(() => {
      if (stopped) return;
      if (typeof requestIdleCallback === 'function') requestIdleCallback(run, { timeout: 500 });
      else setTimeout(run, 40);
    });
  };
  const enter = card => {
    near.add(card);
    const active = card.querySelector('.c-collection-card__image--active');
    if (active && !active.complete) {
      active.addEventListener('load', schedule, { once: true });
      active.addEventListener('error', schedule, { once: true });
    }
    card.querySelectorAll('.cm-search-result-card__image:not(.c-collection-card__image--active)').forEach(image => {
      if (!warmed.has(image) && !queued.has(image)) { queued.add(image); pending.push(image); }
    });
    schedule();
  };
  const intent = event => {
    const swatch = event.target.closest('.cm-search-result-card .c-collection-card__swatch[data-variant-id]');
    if (!swatch || !list.contains(swatch)) return;
    const card = swatch.closest('.cm-search-result-card');
    const image = Array.from(card.querySelectorAll('.cm-search-result-card__image[data-variant-id]'))
      .find(candidate => candidate.dataset.variantId === swatch.dataset.variantId);
    warm(image, 'high');
  };
  for (const type of ['pointerover', 'pointerdown', 'focusin', 'click']) list.addEventListener(type, intent, true);
  let observer;
  const refresh = () => cards.forEach(card => {
    const rect = card.getBoundingClientRect();
    if (rect.bottom >= -200 && rect.top <= innerHeight + 200) enter(card);
    else near.delete(card);
  });
  if (typeof IntersectionObserver === 'function') {
    observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (entry.isIntersecting) enter(entry.target); else near.delete(entry.target);
    }), { rootMargin: '200px 0px' });
    cards.forEach(card => observer.observe(card));
  } else {
    refresh(); window.addEventListener('scroll', refresh, { passive: true }); window.addEventListener('resize', refresh);
  }
  return () => {
    stopped = true; observer?.disconnect(); pending.length = 0; near.clear(); queued.clear();
    window.removeEventListener('scroll', refresh); window.removeEventListener('resize', refresh);
    for (const type of ['pointerover', 'pointerdown', 'focusin', 'click']) list.removeEventListener(type, intent, true);
  };
}

function cardFragment(product, ids, trackedUrl) {
  const html = renderSearchCardHtml(product) || product.cardHtml;
  if (!html) throw new Error('Product card missing');
  const template = document.createElement('template'); template.innerHTML = html;
  const card = template.content.querySelector('.cm-search-result-card');
  if (!card || template.content.querySelector('script')) throw new Error('Product card invalid');
  if (trackedUrl) card.querySelectorAll('.c-collection-card__image-link, .c-collection-card__title a').forEach(link => link.href = trackedUrl);
  const id = ids?.find(id => product.variants.some(v => String(v.id) === String(id) && v.available)) || ids?.[0];
  const variant = product.variants.find(v => String(v.id) === String(id));
  if (id) {
    card.dataset.initialVariantId = id;
    let activeImage = false;
    card.querySelectorAll('.c-collection-card__image-container [data-variant-id]').forEach(img => {
      const selected = String(img.dataset.variantId) === String(id); activeImage ||= selected;
      img.classList.toggle('c-collection-card__image--active', selected); img.style.display = selected ? '' : 'none';
    });
    // A variant can share its image or have no own image; retain the default product image.
    if (!activeImage) { const fallback = card.querySelector('[data-default-image]'); if (fallback) { fallback.style.display = ''; fallback.classList.add('c-collection-card__image--active'); if (variant?.image?.url) { fallback.src = variant.image.url; fallback.removeAttribute('srcset'); fallback.alt = variant.image.alt || product.title; } } }
    let color = '';
    const colorValue = variant?.options?.find(option => /color|colour|カラー|色/i.test(option.name))?.value;
    card.querySelectorAll('.c-collection-card__swatch').forEach(swatch => {
      const selected = String(swatch.dataset.variantId) === String(id) || (colorValue && swatch.dataset.colorValue === colorValue); swatch.classList.toggle('c-collection-card__swatch--active', selected);
      if (selected) color = swatch.dataset.colorValue || '';
    });
    const name = card.querySelector('[data-search-color-name]'), label = card.querySelector('[data-search-selected-color]');
    if (name) name.textContent = color; if (label) label.hidden = !color;
    card.querySelectorAll('.c-collection-card__image-link, .c-collection-card__title a').forEach(link => { const url = new URL(link.getAttribute('href'), location.href); url.searchParams.set('variant', id); link.href = url.href; });
    const price = card.querySelector('.c-collection-card__price');
    if (price && variant) { price.replaceChildren(document.createTextNode(money(variant.price)), element('span', '', '（税込）')); }
  }
  return template.content;
}
function updateUrlFilter(url, name, values) {
  url.searchParams.delete(name); values.filter(v => v !== '').forEach(v => url.searchParams.append(name, v)); url.searchParams.delete('page'); return url;
}
function facetForm(root, host, facets, url, extraFacets = [], priceMax) {
  if (!host.cmOptionTemplates) {
    host.cmOptionTemplates = new Map(); host.cmGroupTemplates = new Map();
    host.cmStyles = [...new Map(Array.from(host.querySelectorAll('style')).map(style => [style.textContent, style.cloneNode(true)])).values()];
    const price = host.querySelector('price-range'); if (price) host.cmPriceTemplate = price.cloneNode(true);
    host.querySelectorAll('input[name^="filter."]').forEach(input => {
      const label = Array.from(host.querySelectorAll('label')).find(n => n.htmlFor === input.id);
      if (label) host.cmOptionTemplates.set(`${input.name}:${input.value}`, label.cloneNode(true));
      const group = input.closest('accordion-disclosure'); if (group) host.cmGroupTemplates.set(input.name, group.cloneNode(true));
    });
  }
  const oldOpen = new Set(Array.from(host.querySelectorAll('details[open]')).map(n => n.dataset.dimension));
  const first = !host.dataset.cmFacetReady; host.dataset.cmFacetReady = 'true';
  const form = element('form', 'facets'); form.action = url.pathname; form.dataset.cmFacetForm = 'true';
  const groups = element('div', 'accordion-list');
  function group(key, label, param) {
    const original = host.cmGroupTemplates.get(param);
    let accordion, details, content;
    if (original) {
      accordion = original.cloneNode(true); details = accordion.querySelector('details'); content = accordion.querySelector('.accordion__content'); content.replaceChildren();
    } else {
      accordion = element('accordion-disclosure', 'accordion'); details = element('details', 'accordion__disclosure group');
      const summary = element('summary'), toggle = element('span', 'accordion__toggle h6'); toggle.append(element('span', '', label), element('span', 'animated-plus group-expanded:rotate')); summary.append(toggle); content = element('div', 'accordion__content'); details.append(summary, content); accordion.append(details);
    }
    details.dataset.dimension = key; details.open = oldOpen.has(key) || (first && !!original?.querySelector('details[open]'));
    const rows = element('div', 'v-stack gap-2.5 cm-search-facet-options'); content.append(rows); groups.append(accordion); return rows;
  }
  function option(content, param, row, key) {
    const control = element('div', 'checkbox-control'); const input = element('input', 'dot-checkbox'); input.type = 'checkbox'; input.name = param; input.value = row.value || row.id || row.key;
    input.id = `${root.id || 'cm-search'}-${host.closest('.facets-drawer') ? 'drawer' : 'sidebar'}-${encodeURIComponent(param)}-${encodeURIComponent(input.value)}`;
    input.checked = row.selected; input.disabled = !row.selected && row.count === 0;
    const original = host.cmOptionTemplates.get(`${param}:${input.value}`) || host.cmOptionTemplates.get(`${param}:${row.label}`);
    const label = original ? original.cloneNode(true) : element('label'); label.htmlFor = input.id;
    if (label.classList.contains('color-swatch') || label.classList.contains('thumbnail-swatch')) {
      input.className = 'sr-only'; label.setAttribute('data-tooltip', `${row.label} (${row.count})`); const text = label.querySelector('.sr-only'); if (text) text.textContent = `${row.label} (${row.count})`;
    } else {
      label.replaceChildren(element('span', 'cm-search-facet__name', row.label), element('span', 'cm-search-facet__count', `(${row.count})`));
    }
    control.append(input, label); content.append(control);
  }
  for (const [key, originalDefinition] of Object.entries(dimensions)) {
    if (key === 'color' && extraFacets.some(f => f.param === 'filter.v.t.shopify.color-pattern')) continue;
    if (key === 'series' && !root.cmNativeFacets?.some(f => f.param === originalDefinition.param)) continue;
    const nativeColor = key === 'color' ? root.cmNativeFacets?.find(f => /^filter\.v\.option\.(color|colour|カラー|色)$/i.test(f.param)) : null;
    const definition = nativeColor ? { ...originalDefinition, param: nativeColor.param } : originalDefinition;
    const rows = facets[key] || []; if (!rows.some(row => row.selected || row.count > 0)) continue;
    const content = group(key, definition.label, definition.param);
    for (const row of rows) option(content, definition.param, row, key);
  }
  for (const facet of extraFacets) {
    if (!facet.values.some(row => row.selected || row.count > 0)) continue;
    const definition = root.cmNativeFacets?.find(d => d.param === facet.param);
    const content = group(facet.param, definition?.label || facet.label, facet.param);
    for (const row of facet.values) option(content, facet.param, row, facet.param);
  }
  const availability = group('available', '在庫', 'filter.v.availability');
  for (const [value, label] of [['1', '在庫あり'], ['0', '在庫なし']]) {
    const stock = element('input', 'switch'); stock.type = 'checkbox'; stock.name = 'filter.v.availability'; stock.value = value; stock.id = `${root.id || 'cm-search'}-${host.closest('.facets-drawer') ? 'drawer' : 'sidebar'}-available-${value}`; stock.checked = url.searchParams.getAll(stock.name).includes(value);
    const stockRow = facets.available?.find(row => row.value === value);
    const stockLabel = element('label', 'text-subdued', stockRow ? `${label} (${stockRow.count})` : label); stockLabel.htmlFor = stock.id;
    const control = element('div', 'checkbox-control'); control.append(stock, stockLabel); availability.append(control);
  }
  const price = group('price', '価格', 'filter.v.price.gte');
  if (host.cmPriceTemplate) {
    const range = host.cmPriceTemplate.cloneNode(true), ceiling = Math.ceil(Number(priceMax || 0) / 100) || Number(range.querySelector('input[type=range]')?.max) || 1000000;
    const min = Number(url.searchParams.get('filter.v.price.gte') || 0), max = Number(url.searchParams.get('filter.v.price.lte') || ceiling);
    range.querySelectorAll('input[type=range]').forEach((input, i) => { input.max = ceiling; input.value = i ? max : min; });
    range.querySelectorAll('input[name]').forEach(input => { input.value = url.searchParams.get(input.name) || ''; input.max = ceiling; input.min = '0'; input.id = `${host.closest('.facets-drawer') ? 'drawer' : 'sidebar'}-${input.name}`; });
    const slider = range.querySelector('.range-group'); if (slider) { slider.style.setProperty('--range-min', `${min / ceiling * 100}%`); slider.style.setProperty('--range-max', `${max / ceiling * 100}%`); }
    price.replaceChildren(range);
  } else {
    for (const [name, label] of [['filter.v.price.gte', '最低価格'], ['filter.v.price.lte', '最高価格']]) {
      const input = element('input', 'input'); input.type = 'number'; input.min = '0'; input.step = '1'; input.name = name; input.value = url.searchParams.get(name) || ''; input.setAttribute('aria-label', label); input.placeholder = `${label}（円）`; price.append(input);
    }
  }
  host.cmStyles.forEach(style => form.append(style.cloneNode(true)));
  form.append(groups);
  form.addEventListener('change', event => { event.stopPropagation(); if (!form.closest('.facets-drawer')) root.cmSearchController.navigate(root.cmSearchController.formUrl(form)); });
  host.replaceChildren(form);
}
class SearchResults {
  constructor(root) {
    this.root = root; root.cmNativeFacets = JSON.parse(root.querySelector('[data-cm-native-facets]')?.textContent || '[]'); root.cmNativeUrls = JSON.parse(root.querySelector('[data-cm-native-product-urls]')?.textContent || '{}'); this.generation = 0; this.initialQuery = readSearchState(location.href).query;
    this.suggestions = element('div'); this.suggestions.dataset.cmResultSuggestions = '';
    root.querySelector('[data-cm-engine-status]').after(this.suggestions);
    root.addEventListener('facet:update', event => { event.stopImmediatePropagation(); if (event.detail?.url) this.navigate(event.detail.url); });
    const submitFacet = event => {
      const form = event.target.closest('[data-cm-facet-form]'); if (!form) return;
      event.preventDefault(); event.stopImmediatePropagation(); this.navigate(this.formUrl(form));
    };
    root.addEventListener('submit', submitFacet, true);
    // Native Drawer moves itself to body while open; listeners must follow its element.
    const drawerId = root.querySelector('button[aria-controls="facets-drawer"]')?.getAttribute('aria-controls');
    this.drawer = root.querySelector('facets-drawer') || (drawerId ? document.getElementById(drawerId) : null);
    this.drawer?.addEventListener('submit', submitFacet, true);
    this.drawer?.addEventListener('click', event => {
      const apply = event.target.closest('[slot="footer"] button');
      if (!apply || !this.drawer.contains(apply)) return;
      const form = this.drawer.querySelector('[data-cm-facet-form]'); if (!form) return;
      event.preventDefault(); event.stopImmediatePropagation();
      this.navigate(this.formUrl(form));
      this.drawer.hide();
    }, true);
    root.addEventListener('click', event => {
      if (event.target.closest('[data-cm-retry]')) {
        if (/dynamically imported module|Importing a module script failed|loading a module script/i.test(root.dataset.engineError || '')) location.reload();
        else this.render(true);
        return;
      }
      const anchor = event.target.closest('[data-cm-pagination] a, [data-cm-active-facets] a, [data-cm-clear]');
      if (anchor && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) { event.preventDefault(); event.stopPropagation(); this.navigate(new URL(anchor.href), { scrollToResults: Boolean(anchor.closest('[data-cm-pagination]')) }); return; }
    });
    window.addEventListener('pageshow', event => { if (event.persisted) this.render(true); });
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') this.render(); });
    window.addEventListener('popstate', () => { this.pendingResultsScroll = false; if (readSearchState(location.href).query !== this.initialQuery) location.reload(); else this.render(); });
    this.render();
  }
  formUrl(form) {
    const url = new URL(location.href);
    for (const key of Array.from(url.searchParams.keys())) if (key.startsWith('filter.')) url.searchParams.delete(key);
    new FormData(form).forEach((value, key) => { if (value !== '') url.searchParams.append(key, value); }); url.searchParams.delete('page'); return url;
  }
  navigate(value, { scrollToResults = false } = {}) {
    const url = new URL(value, location.href); url.searchParams.delete('section_id');
    if (url.searchParams.get('q') !== this.initialQuery) { location.href = url.href; return; }
    if (url.href === location.href) return;
    this.pendingResultsScroll = scrollToResults;
    history.pushState({ cmSearch: true }, '', url); this.render();
  }
  async render(force = false) {
    const renderStartedAt = performance.now();
    this.controller?.abort(); const controller = this.controller = new AbortController(); const generation = ++this.generation;
    const root = this.root, panel = root.querySelector('[data-cm-product-panel]'), status = root.querySelector('[data-cm-engine-status]'), retry = root.querySelector('[data-cm-retry]');
    const count = root.querySelector('[data-cm-count]'), list = root.querySelector('[data-cm-products]');
    root.querySelector('[data-cm-conditions]').hidden = true;
    this.suggestions.replaceChildren(); this.suggestions.hidden = true;
    delete root.dataset.engineError; root.dataset.engineState = 'loading'; root.setAttribute('aria-busy', 'true'); panel.style.visibility = 'hidden'; count.textContent = '—'; status.textContent = '検索商品を読み込み中です。'; retry.hidden = true;
    try {
      const loader = await import(root.dataset.engineLoader); const engine = await loader.loadSearchEngine({ force, signal: controller.signal });
      current(controller.signal); if (generation !== this.generation) return;
      const engineReadyAt = performance.now();
      const url = new URL(location.href), state = readSearchState(url.href, Number(root.dataset.pageSize) || 24);
      if (state.unsupported.length) throw new Error(`未対応の絞り込み条件があります: ${state.unsupported.join('、')}`);
      const result = engine.search(state.query, state.filters, state.options);
      const searchFinishedAt = performance.now();
      const focusedId = document.activeElement?.id;
      const fragment = document.createDocumentFragment();
      for (const product of result.products) fragment.append(cardFragment(product, result.matchedVariantIds?.[product.handle], root.cmNativeUrls[product.handle]));
      current(controller.signal); if (generation !== this.generation) return;
      list.replaceChildren(fragment); list.hidden = !result.products.length;
      const cardsFinishedAt = performance.now();
      root.querySelector('[data-cm-empty]').hidden = result.products.length > 0;
      count.textContent = String(result.total); status.textContent = resultMessage(result);
      this.conditions(result); this.activeFilters(url, result);
      const suggestionUrl = new URL(url); suggestionUrl.searchParams.delete('page');
      if (result.state !== 'resolved') appendSuggestions(this.suggestions, result, suggestionUrl.href); this.suggestions.hidden = !this.suggestions.childElementCount;
      const facetHosts = new Set([...root.querySelectorAll('facets-form'), ...this.drawer?.querySelectorAll('facets-form') || []]);
      facetHosts.forEach(host => facetForm(root, host, result.facets, url, result.extraFacets, engine.metadata?.priceMax));
      this.pagination(result, url);
      root.querySelectorAll('#sort-by-popover [role="option"]').forEach(option => option.setAttribute('aria-selected', String(option.value === result.sort)));
      panel.style.visibility = ''; if (focusedId) document.getElementById(focusedId)?.focus({preventScroll:true}); root.dataset.engineState = result.state || 'ready'; root.setAttribute('aria-busy', 'false');
      document.dispatchEvent(new CustomEvent('cm:search:rendered', { detail: { root } }));
      if (this.pendingResultsScroll) requestAnimationFrame(() => {
        if (generation !== this.generation || controller.signal.aborted || !this.pendingResultsScroll) return;
        this.pendingResultsScroll = false;
        root.querySelector('.c-main-search__main')?.scrollIntoView({ block: 'start', behavior: 'instant' });
      });
      this.stopCardImagePreparation?.(); this.stopCardImagePreparation = prepareSearchCardImages(list);
      const diagnostics = loader.getSearchLoadDiagnostics?.() || {};
      root.dataset.engineSource = diagnostics.source || 'network';
      root.dataset.engineRenderMs = String(Math.round(performance.now() - renderStartedAt));
      if (!this.firstReadyMeasured) {
        this.firstReadyMeasured = true;
        root.dataset.enginePhases = JSON.stringify({loader:Math.round(engineReadyAt-renderStartedAt),search:Math.round(searchFinishedAt-engineReadyAt),cards:Math.round(cardsFinishedAt-searchFinishedAt),facets:Math.round(performance.now()-cardsFinishedAt)});
        root.dataset.engineReadyMs = String(Math.round(performance.now()));
        root.dataset.engineReadyVisibility = document.visibilityState;
        const navigation = performance.getEntriesByType('navigation')[0];
        root.dataset.engineNavigationTiming = JSON.stringify(navigation ? {responseStart: Math.round(navigation.responseStart), responseEnd: Math.round(navigation.responseEnd), domInteractive: Math.round(navigation.domInteractive), domContentLoaded: Math.round(navigation.domContentLoadedEventEnd)} : {});
        root.dataset.engineIndexTiming = JSON.stringify(performance.getEntriesByType('resource').filter(entry => {
          const url = new URL(entry.name, location.href);
          return url.pathname.endsWith('/collections/all') && url.searchParams.get('view') === 'cm-index-v2';
        }).slice(-8).map(entry => ({page: new URL(entry.name).searchParams.get('page'), start: Math.round(entry.startTime), end: Math.round(entry.responseEnd), duration: Math.round(entry.duration)})));
        root.dataset.engineAssetTiming = JSON.stringify(performance.getEntriesByType('resource').filter(entry => /\/cm-search-(data|engine|ui)\.js(?:\?|$)/.test(entry.name)).slice(-6).map(entry => ({file: new URL(entry.name).pathname.split('/').pop(), start: Math.round(entry.startTime), end: Math.round(entry.responseEnd)})));
      }
      if (!this.firstRenderMeasured) requestAnimationFrame(() => requestAnimationFrame(() => {
        if (generation !== this.generation || this.firstRenderMeasured || controller.signal.aborted) return;
        this.firstRenderMeasured = true;
        root.dataset.engineNavigationMs = String(Math.round(performance.now()));
        root.dataset.enginePaintVisibility = document.visibilityState;
        root.dataset.engineInitialSource = diagnostics.navigationSource || diagnostics.source || 'network';
        root.dataset.engineMeasuredQuery = state.query;
        root.dataset.engineMeasuredCount = String(result.total);
        const image = list.querySelector('.c-collection-card__image--active');
        const markImage = () => requestAnimationFrame(() => {
          if (generation === this.generation && image?.isConnected) root.dataset.engineFirstImageMs = String(Math.round(performance.now()));
        });
        if (image?.complete && image.naturalWidth > 0) markImage();
        else image?.addEventListener('load', markImage, { once: true });
      }));

    } catch (error) {
      if (controller.signal.aborted || generation !== this.generation) return;
      root.dataset.engineError = String(error?.message || error).slice(0,200); root.dataset.engineState = 'error'; root.setAttribute('aria-busy', 'false'); status.textContent = error.message?.startsWith('未対応') ? error.message : '検索商品を読み込めませんでした。もう一度お試しください。'; retry.hidden = false;
    }
  }
  conditions(result) {
    const host = this.root.querySelector('[data-cm-conditions]'); host.replaceChildren();
    const labels = [];
    for (const [key, rows] of Object.entries(result.intent?.dimensions || {})) {
      const label = dimensions[key]?.label || (key === 'audience' ? '対象' : null);
      if (label) rows.forEach(row => labels.push(`${label}: ${row.label}`));
    }
    const conditions = result.intent?.conditions || {};
    if (conditions.minPrice != null || conditions.maxPrice != null) labels.push(`価格: ${conditions.minPrice == null ? '' : money(conditions.minPrice)}〜${conditions.maxPrice == null ? '' : money(conditions.maxPrice)}`);
    if (conditions.available === true) labels.push('在庫あり');
    labels.forEach(label => host.append(element('span', 'removable-facet text-subdued', label))); host.hidden = !labels.length;
  }
  activeFilters(url, result) {
    const host = this.root.querySelector('[data-cm-active-facets]'); host.replaceChildren();
    for (const [name, value] of url.searchParams) {
      if (!name.startsWith('filter.') || !value) continue;
      const key = Object.keys(dimensions).find(k => dimensions[k].param === name) || (/^filter\.v\.option\./.test(name) ? 'color' : '');
      const extra = result.extraFacets?.find(facet => facet.param === name);
      const row = result.facets[key]?.find(row => [row.id, row.key, row.label].includes(value)) || extra?.values.find(row => row.value === value);
      const label = row?.label || (name === 'filter.v.availability' ? value === '1' ? '在庫あり' : '在庫なし' : /filter.v.price/.test(name) ? `${name.endsWith('gte') ? '最低' : '最高'}価格: ${money(Number(value) * 100)}` : value);
      const removeUrl = new URL(url); const retained = removeUrl.searchParams.getAll(name).filter(v => v !== value); updateUrlFilter(removeUrl, name, retained);
      const chip = element('div', 'removable-facet'); const remove = element('a', 'tap-area link-faded', '×'); remove.href = removeUrl.href; remove.setAttribute('aria-label', `${label}を解除`); chip.append(remove, element('span', 'text-subdued', label)); host.append(chip);
    }
    host.hidden = !host.childElementCount;
    if (!host.hidden) { const clear = new URL(url); for (const key of Array.from(clear.searchParams.keys())) if (key.startsWith('filter.')) clear.searchParams.delete(key); clear.searchParams.delete('page'); const link = element('a', 'facets-clear-all text-subdued link', 'すべて解除'); link.href = clear.href; host.append(link); }
  }
  pagination(result, url) {
    const nav = this.root.querySelector('[data-cm-pagination]'); nav.replaceChildren(); nav.hidden = result.pages <= 1;
    for (let page = 1; page <= result.pages; page++) {
      const link = element(page === result.page ? 'span' : 'a', `pagination__link h6${page === result.page ? ' pagination__link--disabled' : ''}`, String(page));
      if (page === result.page) link.setAttribute('aria-current', 'page'); else { const target = new URL(url); target.searchParams.set('page', page); link.href = target.href; }
      link.setAttribute('aria-label', `${page}ページへ`); nav.append(link);
    }
  }
}
if (typeof document !== 'undefined') document.querySelectorAll('[data-cm-search-results]').forEach(root => { if (!root.cmSearchController) root.cmSearchController = new SearchResults(root); });
